/**
 * SafeSpacePanel — kid's quiet listening-ear chat.
 *
 * Behaviour (Phase 2.18):
 *  - Always opens FRESH (no auto-resume of last conversation). When the panel
 *    closes, the on-screen chat clears. The student can pull up any past
 *    session via the "Past chats" picker.
 *  - Tagline is "I am listening…" (no trust-breaking language).
 *  - Backend handles refusals/distress via the system prompt.
 *  - Iter 78: we never claim this chat is private or unseen. The empty state
 *    carries one honest, age-appropriate line: chats are saved, the care team
 *    can read them, and a grown-up is told if the child may be in danger.
 */
import { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BuddyAvatar, BUDDY_ROSTER } from '@/components/BuddyRoster';
import { Heart, Send, Sparkles, History, ChevronLeft, MessageCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PROMPT_STARTERS = [
  "Today felt hard.",
  "Something happened at school today…",
  "I just want to talk.",
  "I'm feeling tired.",
  "Can I ask you something?",
];

function fmtRelative(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function SafeSpacePanel({ open, onOpenChange, onOpenFeedback }) {
  const { user } = useAuth();
  const buddyId = (user?.preferred_buddy && BUDDY_ROSTER[user.preferred_buddy]) ? user.preferred_buddy : 'chirpy';
  const buddy = BUDDY_ROSTER[buddyId];

  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Always FRESH on open — clear local state. No auto-resume of last session.
  useEffect(() => {
    if (!open) {
      // Defer clear so close animation looks tidy
      const t = setTimeout(() => {
        setMessages([]); setSessionId(null); setInput('');
        setShowHistory(false); setPastSessions([]);
      }, 250);
      return () => clearTimeout(t);
    }
    setMessages([]); setSessionId(null); setShowHistory(false);
    apiGet('/safe-space/quota').then(setQuota).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingHistory(true);
    try {
      const r = await apiGet('/safe-space/sessions?limit=20');
      setPastSessions(r.sessions || []);
    } catch { toast.error('Could not load past chats.'); }
    finally { setLoadingHistory(false); }
  };

  const loadPastSession = async (sid) => {
    setLoadingHistory(true);
    try {
      const r = await apiGet(`/safe-space/messages?session_id=${encodeURIComponent(sid)}`);
      setSessionId(r.session_id);
      setMessages(r.messages || []);
      setShowHistory(false);
    } catch { toast.error('Could not retrieve that chat.'); }
    finally { setLoadingHistory(false); }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    setInput('');
    const tempUser = { message_id: `tmp_${Date.now()}`, role: 'user', content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUser]);
    try {
      const r = await apiPost('/safe-space/chat', { content, session_id: sessionId, buddy_id: buddyId });
      setSessionId(r.session_id);
      if (r.quota) setQuota(q => ({ ...q, ...r.quota, remaining: Math.max(0, r.quota.limit - r.quota.used) }));
      setMessages(prev => {
        const without = prev.filter(m => m.message_id !== tempUser.message_id);
        return [...without, r.user_message, r.assistant_message];
      });
      setTimeout(() => inputRef.current?.focus(), 60);
    } catch (e) {
      const msg = (e?.message || '').replace(/^HTTP \d+:\s*/, '');
      const isCap = e?.status === 429 || msg.includes('safe-space messages') || msg.includes('Request failed: 429');
      if (isCap) {
        const friendly = msg && !msg.startsWith('Request failed:')
          ? msg
          : "We've hit your quiet-chat limit for today. It refreshes tomorrow morning. If you'd like to talk to a real person right now, Tele-MANAS is free and open any time, day or night — 14416. I'll be right here too.";
        const capMsg = { message_id: `cap_${Date.now()}`, role: 'assistant', content: friendly, created_at: new Date().toISOString() };
        setMessages(prev => [...prev.filter(m => m.message_id !== tempUser.message_id), tempUser, capMsg]);
        apiGet('/safe-space/quota').then(setQuota).catch(() => {});
      } else {
        toast.error('Could not send. Try again in a moment.');
        setMessages(prev => prev.filter(m => m.message_id !== tempUser.message_id));
        setInput(content);
      }
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (sid) => {
    try {
      await apiDelete(`/safe-space/sessions/${sid}`);
      setPastSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sid === sessionId) { setSessionId(null); setMessages([]); }
      toast.success('That chat is gone.');
    } catch {
      toast.error('Could not delete that chat. Try again in a moment.');
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg p-0 border-l-2 border-rose-100 flex flex-col"
        data-testid="safe-space-panel">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 p-5 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {showHistory ? (
              <button onClick={() => setShowHistory(false)} data-testid="back-from-history"
                className="w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-slate-50">
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
            ) : (
              <div className="w-12 h-12 rounded-2xl overflow-hidden bg-white shadow-md flex items-center justify-center flex-shrink-0">
                <BuddyAvatar buddyId={buddyId} size={48} mood="consoling" />
              </div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <SheetTitle className="font-display text-lg font-bold text-slate-900 flex items-center gap-1.5">
                {showHistory ? 'Past chats' : (
                  <>
                    {buddy?.name || 'Saathi'} <Heart className="w-3.5 h-3.5 text-rose-500" fill="currentColor" />
                  </>
                )}
              </SheetTitle>
              <p className="text-[12px] text-slate-600 font-body mt-0.5 italic">
                {showHistory ? 'Pick a chat to retrieve' : 'I am listening…'}
              </p>
            </div>
            {!showHistory && (
              <>
                <button onClick={openHistory} data-testid="open-history"
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-slate-50"
                  title="Past chats">
                  <History className="w-4 h-4 text-slate-600" />
                </button>
                {quota && !quota.bypass && (
                  <div data-testid="safe-space-quota"
                    className={`flex-shrink-0 text-right px-2.5 py-1 rounded-xl ${
                      quota.remaining <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-white/70 text-slate-600'
                    }`}>
                    <p className="text-[9px] font-heading uppercase tracking-wider leading-none">Today</p>
                    <p className="text-xs font-heading font-bold leading-tight">{quota.used} / {quota.limit}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetHeader>

        {/* History list */}
        {showHistory ? (
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50/40" data-testid="history-list">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-rose-300 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : pastSessions.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-body">No past chats yet. They'll appear here once you've talked to me a few times.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pastSessions.map(s => (
                  <div key={s.session_id}
                    className="group relative rounded-2xl bg-white border border-slate-100 hover:border-rose-200 hover:bg-rose-50/40 transition-all">
                    <button data-testid={`past-session-${s.session_id}`}
                      onClick={() => loadPastSession(s.session_id)}
                      className="w-full text-left p-3 pr-11">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-heading text-sm font-bold text-slate-800 capitalize">
                          {s.buddy_id === 'redpanda' ? 'Red Panda' : (s.buddy_id || 'Saathi')}
                        </p>
                        <span className="text-[10px] text-slate-400 font-body ml-auto">{fmtRelative(s.last_message_at)}</span>
                      </div>
                      <p className="text-xs text-slate-600 font-body line-clamp-2 italic">"{s.preview || '(empty)'}"</p>
                      <p className="text-[10px] text-slate-400 font-body mt-1">{s.message_count} messages</p>
                    </button>
                    <button data-testid={`delete-session-${s.session_id}`}
                      aria-label="Delete this chat"
                      onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                      className="absolute top-2.5 right-2.5 p-2 rounded-xl text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-gradient-to-b from-white to-rose-50/30"
              data-testid="safe-space-messages">
              {messages.length === 0 ? (
                <div className="text-center pt-8 pb-4" data-testid="safe-space-empty">
                  <div className="inline-block w-20 h-20 rounded-2xl overflow-hidden bg-white shadow-md mb-3">
                    <BuddyAvatar buddyId={buddyId} size={80} mood="happy" />
                  </div>
                  <p className="font-display text-base font-bold text-slate-800 mb-1">Hey, it's me — {buddy?.name || 'Saathi'}.</p>
                  <p className="text-sm text-slate-600 font-body max-w-xs mx-auto leading-relaxed">
                    Whatever's on your mind today, I'm here to listen. There's nothing too small or too big to share.
                  </p>
                  <p data-testid="safe-space-honesty-note"
                     className="text-[11px] text-slate-500 font-body max-w-[300px] mx-auto leading-relaxed mt-3 bg-white/70 border border-rose-100 rounded-2xl px-3 py-2">
                    One honest thing, so you always know: these chats are saved to your
                    account, and you can open them again from "Past chats". Our care team
                    can read them. If it ever looks like you might be in danger or being
                    hurt, they are told so a grown-up can help you. Otherwise, I'm here
                    to listen — and you can delete any chat from "Past chats" whenever
                    you want.
                  </p>
                  <div className="flex flex-col items-stretch gap-2 mt-5 max-w-[280px] mx-auto">
                    {PROMPT_STARTERS.map((p, i) => (
                      <button key={i} data-testid={`safe-space-starter-${i}`}
                        onClick={() => send(p)} disabled={sending}
                        className="text-left px-4 py-2.5 rounded-2xl bg-white border border-rose-100 hover:border-rose-300 hover:bg-rose-50/50 transition-all text-sm font-body text-slate-700 shadow-sm">
                        {p}
                      </button>
                    ))}
                  </div>
                  {onOpenFeedback && (
                    <button data-testid="safe-space-feedback-link"
                      onClick={onOpenFeedback}
                      className="mt-5 text-xs font-body text-slate-500 hover:text-rose-600 underline decoration-dotted underline-offset-2 transition-colors">
                      Got feedback about the app? Share it here →
                    </button>
                  )}
                </div>
              ) : (
                messages.map(m => <Bubble key={m.message_id} msg={m} buddyId={buddyId} />)
              )}
              {sending && (
                <div className="flex items-end gap-2" data-testid="safe-space-typing">
                  <div className="w-8 h-8 rounded-2xl overflow-hidden bg-white border border-slate-100 flex items-center justify-center flex-shrink-0">
                    <BuddyAvatar buddyId={buddyId} size={32} mood="thinking" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-white border border-slate-100">
                    <div className="flex gap-1.5 items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '240ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="flex-shrink-0 p-3 sm:p-4 bg-white border-t border-slate-100">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  data-testid="safe-space-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Type whatever's on your mind…"
                  disabled={sending}
                  className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 focus:border-rose-300 focus:ring-2 focus:ring-rose-100 outline-none resize-none text-sm font-body bg-white max-h-[140px]"
                  style={{ minHeight: 44 }}
                />
                <button
                  data-testid="safe-space-send"
                  onClick={() => send()}
                  disabled={sending || !input.trim()}
                  className="flex-shrink-0 w-11 h-11 rounded-2xl bg-rose-500 hover:bg-rose-600 disabled:bg-slate-200 text-white flex items-center justify-center transition-all shadow-sm">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-slate-400 font-body mt-2 text-center inline-flex items-center justify-center gap-1 w-full">
                <Sparkles className="w-2.5 h-2.5" />
                Need a real person? Tele-MANAS — 14416, free, any time of day.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Bubble({ msg, buddyId }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`} data-testid={`bubble-${msg.role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-2xl overflow-hidden bg-white border border-slate-100 flex items-center justify-center flex-shrink-0">
          <BuddyAvatar buddyId={buddyId} size={32} mood={msg.distress_flagged ? 'consoling' : 'idle'} />
        </div>
      )}
      <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm font-body leading-relaxed whitespace-pre-wrap ${
        isUser
          ? 'bg-gradient-to-br from-rose-500 to-pink-500 text-white rounded-br-sm shadow-sm'
          : msg.distress_flagged
            ? 'bg-rose-50 border border-rose-200 text-slate-800 rounded-bl-sm'
            : 'bg-white border border-slate-100 text-slate-800 rounded-bl-sm shadow-sm'
      }`}>
        {msg.content}
      </div>
    </div>
  );
}
